/**
 * FlowTrackers — невидимые компоненты, эмитящие UX flow markers:
 *
 *   1. **NavigationTracker** — следит за useNavigationStore.current,
 *      эмитит `screen.entered` / `screen.exited` с длительностью.
 *      На session_start первый screen помечается с source='session_start'.
 *
 *   2. **VisibilityTracker** — слушает Page Visibility API. Эмитит
 *      `visibility.changed` при переключении вкладки/окна. Pause-resume
 *      сессии видим через эти события.
 *
 *   3. **IdleTracker** — если игрок > IDLE_THRESHOLD_MS ничего не делает
 *      на текущем экране (нет тапов, нет переключений), эмитит
 *      `idle.entered` ОДИН РАЗ. Reset на каждое взаимодействие.
 *
 *   4. **SessionTracker** — emit session.end на закрытие вкладки или
 *      переход на hidden (mobile часто не стреляет beforeunload). Считает
 *      duration_sec + taps_in_session (∆ totalTaps от mount'а до закрытия).
 *
 * Mount в App.tsx как `<FlowTrackers />`. Renders null — pure side-effect.
 *
 * Все события проходят через `emit()` который сам gate'нет по consent
 * (см. `telemetry/consent.ts`).
 */
import { useEffect, useRef } from 'react';
import { useGameStore } from '../stores/game.js';
import { useNavigationStore, type AppScreen } from '../screens/navigation.js';
import { pullStateOnce, pushStateDebounced, pushStateNow } from './cloudSync.js';
import { emit } from './emit.js';
import { flushNow, startFlushScheduler } from './flush.js';
import { markFunnel } from './funnel.js';
import { verifyTgIdentityOnce } from './tgAuth.js';

/** Сколько ms бездействия (без тапов) считается idle. */
const IDLE_THRESHOLD_MS = 30_000;
/** Минимальный интервал между idle.entered emit'ами per session. */
const IDLE_DEBOUNCE_MS = 60_000;

export function FlowTrackers(): JSX.Element {
  // Запускаем periodic flush scheduler на mount, останавливаем на unmount.
  // Внутри flush сам gate'нет на VITE_API_BASE + consent — здесь только
  // lifecycle.
  useEffect(() => {
    const stop = startFlushScheduler();
    return stop;
  }, []);

  // TG WebApp auth — Этап 4 backend (2026-05-25). Если игра открыта
  // в Telegram Mini App, на mount верифицируем initData с сервером
  // и сохраняем tg_user_id для future cloud sync через TG identity.
  // Skip silent если игра в обычном browser.
  useEffect(() => {
    void verifyTgIdentityOnce();
  }, []);

  // Cloud save — Этап 3 backend (2026-05-25):
  //   1. На mount: pull server state, если remote newer → restore
  //   2. Subscribe на game state changes → debounced push (10s после
  //      последнего изменения)
  //   3. visibility hidden / unload → immediate push (через SessionTracker)
  useEffect(() => {
    void pullStateOnce();
    const unsub = useGameStore.subscribe((s, prev) => {
      // Push только когда state реально изменился (не UI-only поля).
      // Сравниваем lastActiveAt — он движется на каждом accepted тапе
      // и crystallization'е. Если он не сдвинулся — push не нужен.
      if (s.state.stats.lastActiveAt !== prev.state.stats.lastActiveAt) {
        pushStateDebounced();
      } else if (s.state.purchasedUpgrades !== prev.state.purchasedUpgrades) {
        // Покупка апгрейда не двигает lastActiveAt но меняет state.
        pushStateDebounced();
      } else if (s.state.profile.displayName !== prev.state.profile.displayName) {
        // Игрок ввёл имя.
        pushStateDebounced();
      }
    });
    return unsub;
  }, []);

  return (
    <>
      <NavigationTracker />
      <VisibilityTracker />
      <IdleTracker />
      <SessionTracker />
    </>
  );
}

function SessionTracker(): null {
  // Snapshot taps at session start so мы знаем сколько новых таптов
  // случилось в эту сессию (totalTaps теперь persistится — без
  // baseline мы бы посчитали все таптов за всю жизнь игрока).
  const sessionStartRef = useRef<number>(Date.now());
  const tapsAtSessionStartRef = useRef<number>(useGameStore.getState().state.stats.totalTaps);

  useEffect(() => {
    function emitSessionEnd(): void {
      const now = Date.now();
      const duration_sec = Math.round((now - sessionStartRef.current) / 1000);
      const tapsNow = useGameStore.getState().state.stats.totalTaps;
      const taps_in_session = tapsNow - tapsAtSessionStartRef.current;
      emit('session.end', { duration_sec, taps_in_session });
    }

    // Mobile Safari / Chrome on iOS не всегда стреляют beforeunload.
    // visibilitychange→hidden — более надёжный сигнал что игрок ушёл.
    // Но: если игрок просто свернул вкладку на 5 минут и вернулся —
    // мы запишем session.end и при возврате должны бы начать новую.
    // Пока что simple: emit на каждое hidden, не делаем restart.
    function onVisibilityChange(): void {
      if (document.visibilityState === 'hidden') {
        emitSessionEnd();
        // Synchronous flush с keepalive — pending события (включая
        // session.end который только что эмитнули) уйдут на backend
        // даже если страница сейчас закроется.
        void flushNow();
        // Cloud save: immediate push текущего state перед закрытием.
        pushStateNow();
      }
    }
    function onBeforeUnload(): void {
      emitSessionEnd();
      void flushNow();
      pushStateNow();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);

  return null;
}

function NavigationTracker(): null {
  const screen = useNavigationStore((s) => s.current);
  // Refs нужны чтобы зафиксировать «текущий экран» и «когда вошёл»
  // между renders, не вызывая лишних effects.
  const prevScreenRef = useRef<AppScreen | null>(null);
  const enteredAtRef = useRef<number>(Date.now());

  useEffect(() => {
    const now = Date.now();
    const prev = prevScreenRef.current;
    if (prev === null) {
      // Первый mount — это session_start (см. App.tsx emit'ит session.start).
      emit('screen.entered', { screen, source: 'session_start' });
      if (screen === 'atlas') {
        markFunnel('first_atlas_visit');
      }
      prevScreenRef.current = screen;
      enteredAtRef.current = now;
      return;
    }
    if (prev === screen) return; // no-op идентичный render
    // Игрок переключил экран — emit пары exited/entered.
    emit('screen.exited', { screen: prev, duration_ms: now - enteredAtRef.current });
    emit('screen.entered', { screen, source: 'toggle' });
    if (screen === 'atlas') {
      markFunnel('first_atlas_visit');
    }
    prevScreenRef.current = screen;
    enteredAtRef.current = now;
  }, [screen]);

  return null;
}

function VisibilityTracker(): null {
  useEffect(() => {
    function handle(): void {
      const state: 'visible' | 'hidden' =
        document.visibilityState === 'visible' ? 'visible' : 'hidden';
      emit('visibility.changed', { state });
    }
    document.addEventListener('visibilitychange', handle);
    return () => {
      document.removeEventListener('visibilitychange', handle);
    };
  }, []);

  return null;
}

function IdleTracker(): null {
  const lastTapAt = useGameStore((s) => s.state.stats.lastTapAt);
  const screen = useNavigationStore((s) => s.current);
  const lastIdleEmitAtRef = useRef<number>(0);

  useEffect(() => {
    // Берём «последнее взаимодействие» — это lastTapAt из store. Если
    // null (никогда не тапал), считаем от Date.now() — игрок только
    // зашёл, idle ещё не считается.
    if (lastTapAt === null) return undefined;

    const tid = window.setTimeout(() => {
      const now = Date.now();
      const since = now - lastTapAt;
      if (since < IDLE_THRESHOLD_MS) return; // race condition guard
      if (now - lastIdleEmitAtRef.current < IDLE_DEBOUNCE_MS) return;
      lastIdleEmitAtRef.current = now;
      emit('idle.entered', { screen, since_last_interaction_ms: since });
    }, IDLE_THRESHOLD_MS);

    return () => {
      window.clearTimeout(tid);
    };
  }, [lastTapAt, screen]);

  return null;
}
